import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  Alert, Button, Card, CardBody, Input, Label,
} from 'reactstrap';
import { get } from '../../helpers/api_helper';
import BaseGrid from '../../components/Common/BaseGrid';
import Sale from '../Sales/view';
import { useScanner } from '../../utilities/ScannerContext';
import { Can } from '../../utilities/Can';

const LOOKUP_URL = '/api/sales/by-return-tracking';
const amazonReturnsUrl = (tracking) => (
  'https://sellercentral.amazon.com/gp/returns/list/v2/ref=xx_myr_dnav_xx'
  + `?searchBy=CarrierTrackingId&searchString=${encodeURIComponent(tracking)}`
);

export function ReturnsWorkspace() {
  const { t } = useTranslation();
  const location = useLocation();
  const { registerListener, removeListener } = useScanner();
  const inputRef = useRef(null);
  const request = useRef(0);
  const flowStateRef = useRef(false);
  const [flowState, setFlowState] = useState(false);
  const [scan, setScan] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [saleId, setSaleId] = useState(null);

  const lookup = useCallback(async (value) => {
    const tracking = value.trim();
    if (!tracking) return;
    request.current += 1;
    const { current } = request;
    setScan(tracking);
    setQuery(tracking);
    setLoading(true);
    setError('');
    setResult(null);
    setSaleId(null);
    try {
      const data = await get(LOOKUP_URL, {
        params: {
          q: tracking, page: 1, per: 2, sort: 'id', dir: 'desc',
        },
      });
      if (current !== request.current) return;
      if (!Array.isArray(data.results) || !Number.isFinite(Number(data.total))
        || (Number(data.total) > 0 && data.results.length === 0)) {
        throw new Error(t('The order search returned an invalid response. Please try again.'));
      }
      setResult(Number(data.total));
      if (Number(data.total) === 1) setSaleId(data.results[0].id);
      if (Number(data.total) === 0 && flowStateRef.current) {
        window.open(amazonReturnsUrl(tracking), '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      if (current === request.current) {
        setError(err.response?.data?.error || err.message || t('Unable to find orders.'));
      }
    } finally {
      if (current === request.current) {
        setLoading(false);
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    }
  }, [t]);

  useEffect(() => {
    inputRef.current?.focus();
    registerListener(lookup);
    return () => {
      request.current += 1;
      removeListener(lookup);
    };
  }, [lookup, registerListener, removeListener]);

  useEffect(() => {
    const tracking = new URLSearchParams(location.search).get('q');
    if (tracking?.trim()) lookup(tracking);
  }, [location.search, location.key, lookup]);

  return (
    <div className="page-content">
      <div className="container-fluid">
        <h4>{t('Returns')}</h4>
        <Card style={{ position: 'sticky', top: 70, zIndex: 10 }}>
          <CardBody>
            <div className="form-check form-switch mb-3">
              <Input
                id="returns-flow-state"
                type="switch"
                role="switch"
                checked={flowState}
                onChange={(event) => {
                  flowStateRef.current = event.target.checked;
                  setFlowState(event.target.checked);
                }}
              />
              <Label check for="returns-flow-state">{t('Flow State')}</Label>
            </div>
            <form onSubmit={(event) => { event.preventDefault(); lookup(scan); }}>
              <Label for="returns-tracking">{t('Return tracking number')}</Label>
              <div className="d-flex gap-2">
                <Input
                  id="returns-tracking"
                  style={{ backgroundColor: 'var(--bs-topbar-search-bg)' }}
                  innerRef={inputRef}
                  value={scan}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(event) => setScan(event.target.value)}
                  placeholder={t('Scan or enter a tracking number')}
                />
                <Button type="submit" color="primary" disabled={!scan.trim()}>
                  {t('Find order')}
                </Button>
              </div>
            </form>
            <div aria-live="polite" aria-busy={loading}>
              {loading && <p className="mt-2 mb-0">{t('Searching...')}</p>}
              {error && <Alert color="danger" className="mt-2 mb-0">{error}</Alert>}
              {result !== null && (
                <p className="mt-2 mb-0">{t('{{count}} matching orders for {{tracking}}', { count: result, tracking: query })}</p>
              )}
            </div>
          </CardBody>
        </Card>
        {result > 1 && (
          <Card>
            <CardBody>
              <BaseGrid
                key={query}
                url={LOOKUP_URL}
                search={query}
                className="table table-hover table-bordered"
                config={{
                  columns: {
                    id: { label: 'Order', sort: true, link: { click: (row) => setSaleId(row.id) } },
                    reference1: { label: 'Reference', sort: true },
                    site: { label: 'Site' },
                    customer_email: { label: 'Customer email', sort: true },
                    total_paid: { label: 'Total', sort: true },
                    currency_code: { label: 'Currency' },
                  },
                  pagination: { sort: 'id', direction: 'desc', perPage: 30 },
                }}
              />
            </CardBody>
          </Card>
        )}
        {saleId !== null && (
          <section aria-label={t('Order details')}>
            <Sale key={saleId} saleId={saleId} embedded onSaleChange={setSaleId} />
          </section>
        )}
        {result === 0 && (
          <Card>
            <CardBody>
              <Button
                tag="a"
                color="primary"
                size="lg"
                className="w-100 py-4"
                href={amazonReturnsUrl(query)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('Try Amazon')}
              </Button>
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  );
}

export default function Returns() {
  return (
    <>
      <Can i="see" this="Customer Service"><ReturnsWorkspace /></Can>
      <Can not i="see" this="Customer Service">
        <div className="page-content"><Alert color="danger">Unauthorized</Alert></div>
      </Can>
    </>
  );
}
